import {applyOperation} from '@github-ui/optimistic-connection'
import {connectionMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useCallback, useMemo} from 'react'

import {LABEL_ARCHIVE_TYPE} from './constants/values'
import type {LabelsQueryData} from './types'
import {buildChangeLabelArchiveStateOperation, LABELS_CONNECTION_PATH} from './utils/label-connection-operations'
import {archiveLabelRequest, unarchiveLabelRequest} from './utils/label-requests'

type UseArchiveLabelOptions = {
  labelId: string
  repositoryId: string
  repositoryNameWithOwner: string
  isArchived: boolean
  queryKey?: RouteQueryKey
  setArchiveError: (message: string) => void
  onOptimisticRemove?: () => void
  onRollback?: () => void
}

const ARCHIVE_MUTATION_META_KEY = 'repositoryLabelArchive'

function getQueryPathname(queryKey: QueryKey): string | undefined {
  const queryDeps = queryKey[4]
  if (!queryDeps || typeof queryDeps !== 'object' || Array.isArray(queryDeps) || !('pathname' in queryDeps)) return
  return typeof queryDeps.pathname === 'string' ? queryDeps.pathname : undefined
}

function isArchivedQuery(queryKey: QueryKey): boolean {
  const queryDeps = queryKey[4]
  if (!queryDeps || typeof queryDeps !== 'object' || Array.isArray(queryDeps) || !('searchParams' in queryDeps)) {
    return false
  }
  const {searchParams} = queryDeps
  return (
    typeof searchParams === 'object' &&
    searchParams !== null &&
    !Array.isArray(searchParams) &&
    'archiveType' in searchParams &&
    searchParams.archiveType === LABEL_ARCHIVE_TYPE.ARCHIVED
  )
}

/**
 * Returns query data with both repository label archive counts replaced.
 * Helper for optimistic updates and rollbacks to avoid clobbering concurrent archive state changes.
 *
 * @param data The current query data.
 * @param archivedCount The new archived count.
 * @param unarchivedCount The new unarchived count.
 * @returns The updated query data with the new archive counts.
 * */
function setArchiveCounts(
  data: LabelsQueryData | undefined,
  archivedCount: number,
  unarchivedCount: number,
): LabelsQueryData | undefined {
  const payloadData = data?.payload.data
  const labels = payloadData?.repository.labels
  if (!data || !payloadData || !labels) return data

  return {
    ...data,
    payload: {
      ...data.payload,
      data: {
        ...payloadData,
        repository: {
          ...payloadData.repository,
          labels: {
            ...labels,
            archivedCount,
            unarchivedCount,
          },
        },
      },
    },
  }
}

/**
 * Replaces nullable deployment-skew counts with the selected variant's equivalent fallback values.
 *
 * @param data The current query data.
 * @param isArchivedView Whether this query variant displays archived labels.
 * @returns The query data with nullable archive counts initialized.
 */
function seedArchiveCounts(data: LabelsQueryData | undefined, isArchivedView: boolean): LabelsQueryData | undefined {
  const labels = data?.payload.data?.repository.labels
  if (!labels || (labels.archivedCount !== null && labels.unarchivedCount !== null)) return data

  return setArchiveCounts(
    data,
    labels.archivedCount ?? (isArchivedView ? labels.totalCount : 0),
    labels.unarchivedCount ?? (isArchivedView ? 0 : labels.totalCount),
  )
}

export function useArchiveLabel({
  labelId,
  repositoryId,
  repositoryNameWithOwner,
  isArchived,
  queryKey,
  setArchiveError,
  onOptimisticRemove,
  onRollback,
}: UseArchiveLabelOptions) {
  const queryClient = useQueryClient()
  const action = isArchived ? 'unarchive' : 'archive'
  const operation = useMemo(() => buildChangeLabelArchiveStateOperation(labelId, isArchived), [isArchived, labelId])
  const mutationKey = useMemo(() => connectionMutationKey(repositoryId, LABELS_CONNECTION_PATH), [repositoryId])

  const {mutate, isPending} = useConnectionMutation<void, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    meta: {[ARCHIVE_MUTATION_META_KEY]: true},
    mutationFn: () =>
      isArchived
        ? unarchiveLabelRequest(repositoryNameWithOwner, labelId)
        : archiveLabelRequest(repositoryNameWithOwner, labelId),
    onMutate: async () => {
      if (!queryKey) return

      const pathname = getQueryPathname(queryKey)
      const sourceQueryHash = hashKey(queryKey)
      const queryFilter = {
        queryKey: queryKey.slice(0, 4),
        predicate: (query: {queryKey: QueryKey}) =>
          pathname !== undefined && getQueryPathname(query.queryKey) === pathname,
      }
      await queryClient.cancelQueries(queryFilter)
      const affectedQueryKeys = queryClient.getQueriesData<LabelsQueryData>(queryFilter).map(([key]) => key)
      for (const key of affectedQueryKeys) {
        queryClient.setQueryData<LabelsQueryData>(key, data =>
          seedArchiveCounts(data, hashKey(key) === sourceQueryHash ? isArchived : isArchivedQuery(key)),
        )
      }
    },
    onError: error => {
      setArchiveError(error.message)
      requestAnimationFrame(() => onRollback?.())
    },
    onSuccess: async () => {
      if (queryKey) {
        await queryClient.cancelQueries({queryKey})
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, operation) : base,
        )
      }
    },
    onSettled: () => {
      const pendingArchiveCount = queryClient
        .getMutationCache()
        .findAll({mutationKey, status: 'pending'})
        .filter(mutation => mutation.options.meta?.[ARCHIVE_MUTATION_META_KEY]).length
      if (!queryKey || pendingArchiveCount > 1) return

      const pathname = getQueryPathname(queryKey)
      const sourceQueryHash = hashKey(queryKey)
      void queryClient.invalidateQueries({
        queryKey: queryKey.slice(0, 4),
        predicate: query =>
          pathname !== undefined &&
          getQueryPathname(query.queryKey) === pathname &&
          hashKey(query.queryKey) !== sourceQueryHash,
        refetchType: 'all',
      })
    },
  })

  const updateArchiveState = useCallback(() => {
    setArchiveError('')
    onOptimisticRemove?.()
    mutate({operation})
  }, [mutate, onOptimisticRemove, operation, setArchiveError])

  return {mutate: updateArchiveState, isPending, action}
}
